import { forwardRef, type ComponentProps } from 'react';
import { Separator } from './separator';
import { cn } from './helpers';
import { XIcon } from 'lucide-react';

/** Main Chip container for a group of filters */
const Chip = ({ children, className, ...props }: ComponentProps<'div'>) => {
  return (
    <div
      className={cn('flex items-center gap-2', className)}
      {...props}
    >
      {children}
    </div>
  );
};

/** Vertical separator between facet and values */
const ChipSeparator = () => {
  return (
    <Separator
      orientation='vertical'
      className='min-h-3.5 bg-gray-200'
    />
  );
};

const ChipItem = forwardRef<
  React.ElementRef<'div'>,
  React.ComponentPropsWithoutRef<'div'>
>(({ className, ...props }, ref) => (
  <div
    className={cn(
      'inline-flex items-center gap-2 border border-pale-border bg-white p-1 text-xs font-medium text-gray-800 rounded-md transition-all',
      className
    )}
    ref={ref}
    {...props}
  />
));
ChipItem.displayName = 'ChipItem';

/** The "Category" part of the chip (e.g., "Assignee:") */
const ChipItemFacet = ({
  children,
  className,
  ...props
}: ComponentProps<'span'>) => {
  return (
    <span
      className={cn('capitalize after:content-[":"] after:ml-0.5', className)}
      {...props}
    >
      {children}
    </span>
  );
};

/** List of values within a chip item */
const ChipItemValuesList = ({
  children,
  className,
  ...props
}: ComponentProps<'div'>) => {
  return (
    <div
      className={cn('flex items-center gap-1', className)}
      {...props}
    >
      {children}
    </div>
  );
};

/** Individual value within the list */
const ChipItemValueItem = ({
  children,
  className,
  ...props
}: ComponentProps<'span'>) => {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 bg-primary-50 rounded-sm px-2 py-[2px] font-medium',
        className
      )}
      {...props}
    >
      {children}
    </span>
  );
};

/** Close button for a value or chip */
const ChipItemClose = ({ className, ...props }: ComponentProps<'span'>) => {
  return (
    <span
      className={cn(
        'inline-flex cursor-pointer opacity-70 hover:opacity-100 transition-colors',
        className
      )}
      {...props}
    >
      <XIcon size={12} />
    </span>
  );
};

export {
  Chip,
  ChipSeparator,
  ChipItem,
  ChipItemClose,
  ChipItemFacet,
  ChipItemValuesList,
  ChipItemValueItem,
};
